import React, { useCallback, useEffect, useRef, useState } from "react";
import { createRoot } from "react-dom/client";
import { isMobile } from "react-device-detect";

// Assets
import IconClosedCaptions from "../../../../../../Assets/SVGs/Icons/VideoPlayer/ClosedCaptions";
import IconCheckMarkHollow from "../../../../../../Assets/SVGs/Icons/YesOrNo/CheckMarkHollow";

// Hooks
import { ISubtitleTrackOption } from "../../../../../../Hooks/Content/Watch/VideoSingle/useSubtitleTracks";

interface VideoSubtitlesProps {
    subtitleTracks: ISubtitleTrackOption[];
    activeLanguage: string;
    isShowing: boolean;
    onToggle: () => void;
    onSelect: (language: string) => void;
    isOpen: boolean;
    onToggleOpen?: (value: boolean) => void;
}

const VideoSubtitles: React.FC<VideoSubtitlesProps> = (
    {
        subtitleTracks,
        activeLanguage,
        isShowing,
        onToggle,
        onSelect,
        isOpen,
        onToggleOpen
    }
) => {
    const containerRef = useRef<HTMLDivElement | null>(null);
    const buttonRef = useRef<HTMLButtonElement | null>(null);
    // Refs used for mobile portal
    const portalContainerRef = useRef<HTMLElement | null>(null);
    const popupRootRef = useRef<ReturnType<typeof createRoot> | null>(null);
    const [containerHeight, setContainerHeight] = useState<string>("auto");

    // With a single track the button is a plain on/off toggle; with multiple
    // tracks it opens a language popup matching the settings menu experience
    const hasLanguageChoice = subtitleTracks.length > 1;

    const handleButtonClick = () => {
        if (hasLanguageChoice) {
            onToggleOpen?.(!isOpen);
        } else {
            onToggle();
        }
    };

    const handleSelect = (language: string) => {
        onSelect(language);
        onToggleOpen?.(false);
    };

    const measureHeight = useCallback(() => {
        if (!containerRef.current) return;
        const list = containerRef.current.querySelector(".settings-popup__slide-in");
        if (list && list instanceof HTMLElement) {
            setContainerHeight(`${list.scrollHeight}`);
        }
    }, []);

    useEffect(() => {
        // Use a slight delay to ensure the DOM updates before measuring.
        const timeoutId = setTimeout(() => {
            measureHeight();
        }, 50);
        return () => clearTimeout(timeoutId);
    }, [isOpen, subtitleTracks, measureHeight]);

    // On mobile, create a portal container outside the video player.
    useEffect(() => {
        if (isMobile) {
            const portalContainer = document.createElement("div");
            portalContainer.className = "video-settings-portal-container";
            document.body.appendChild(portalContainer);
            portalContainerRef.current = portalContainer;
            popupRootRef.current = createRoot(portalContainer);
        }
        return () => {
            if (portalContainerRef.current) {
                if (popupRootRef.current) {
                    popupRootRef.current.unmount();
                }
                document.body.removeChild(portalContainerRef.current);
            }
        };
    }, []);

    // Update portal container classes when isOpen changes.
    useEffect(() => {
        if (isMobile && portalContainerRef.current) {
            if (isOpen) {
                portalContainerRef.current.classList.add("is-open");
                portalContainerRef.current.classList.remove("is-closed");
            } else {
                portalContainerRef.current.classList.add("is-closed");
                portalContainerRef.current.classList.remove("is-open");
            }
        }

        const handleOutsideClick = (event: MouseEvent | TouchEvent) => {
            if (
                containerRef.current && !containerRef.current.contains(event.target as Node) &&
                buttonRef.current && !buttonRef.current.contains(event.target as Node)
            ) {
                onToggleOpen?.(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleOutsideClick);
            document.addEventListener('touchstart', handleOutsideClick);
        }

        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
            document.removeEventListener('touchstart', handleOutsideClick);
        };
    }, [isOpen]);

    const popupContent = (
        <div
            ref={containerRef}
            className={`video-controls__settings-popup video-controls__subtitles-popup ${
                isOpen ? "is-open" : "is-closed"
            }`}
            style={{ height: `${containerHeight}px` }}
        >
            <div className="settings-popup__slide-in is-active">
                <div
                    className={`settings-popup__submenu-item ${
                        activeLanguage === "" ? "is-active" : ""
                    }`}
                    onClick={() => handleSelect("")}
                >
                    Off{" "}
                    {activeLanguage === "" ? IconCheckMarkHollow({ className: "is-active-icon" }) : null}
                </div>

                {subtitleTracks.map((track) => (
                    <div
                        key={track.language}
                        className={`settings-popup__submenu-item ${
                            activeLanguage === track.language ? "is-active" : ""
                        }`}
                        onClick={() => handleSelect(track.language)}
                    >
                        {track.label}{" "}
                        {activeLanguage === track.language
                            ? IconCheckMarkHollow({ className: "is-active-icon" })
                            : null}
                    </div>
                ))}
            </div>
        </div>
    );

    // On mobile, render the popup via our portal using createRoot.
    useEffect(() => {
        if (isMobile && popupRootRef.current && hasLanguageChoice) {
            popupRootRef.current.render(popupContent);
        }
    }, [
        popupContent,
        isOpen,
        containerHeight,
        subtitleTracks,
        activeLanguage,
        hasLanguageChoice,
    ]);

    if (subtitleTracks.length === 0) return null;

    return (
        <>
            {/* On desktop, render inline; on mobile, the popup is rendered via the portal */}
            {!isMobile && hasLanguageChoice && popupContent}
            <button
                ref={buttonRef}
                className={`video-controls__button video-controls--subtitles ${isShowing ? "is-active" : "is-not-active"}`}
                onClick={handleButtonClick}
                aria-label={
                    hasLanguageChoice
                        ? "Subtitle Options"
                        : isShowing ? "Disable Subtitles" : "Enable Subtitles"
                }
            >
                {IconClosedCaptions()}
            </button>
        </>
    );
};

export default VideoSubtitles;
